import { useEffect, useMemo, useState } from 'react'
import { Gear as Settings } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import type { ParametroSistema } from '@/lib/typesNeoCore'

export function NeoCoreParametrosPage() {
  const [rows, setRows] = useState<ParametroSistema[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    supabase
      .from('parametro_sistema')
      .select('*')
      .order('clave', { ascending: true })
      .then(({ data, error }) => {
        if (error) setError(error.message)
        else setRows((data ?? []) as ParametroSistema[])
        setLoading(false)
      })
  }, [])

  // Agrupar por módulo (primera parte de la clave: kyc.* / feis.* / referencia.*)
  const groups = useMemo(() => {
    const map = new Map<string, ParametroSistema[]>()
    for (const r of rows) {
      const mod = r.clave.split('.')[0]
      const list = map.get(mod) ?? []
      list.push(r)
      map.set(mod, list)
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [rows])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <p style={{ fontSize: 13, color: 'var(--n-500)', margin: 0, maxWidth: 720, lineHeight: 1.6 }}>
        Configuración global de NeoCore. Umbrales, modos de operación, defaults.
        Las ediciones quedan registradas en histórico append-only.
        La edición desde esta vista se habilita en una próxima iteración —
        por ahora se cambian con migración SQL.
      </p>

      {error && (
        <div className="error-banner" style={{ margin: 0 }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ padding: 24, color: 'var(--n-500)', fontSize: 13 }}>Cargando…</div>
      )}

      {!loading && groups.map(([modulo, params]) => (
        <div key={modulo} className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div
            style={{
              padding: '12px 18px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--bg)',
            }}
          >
            <Settings size={14} weight="regular" color="var(--n-500)" />
            <h3
              style={{
                fontSize: 13,
                fontWeight: 600,
                margin: 0,
                color: 'var(--n-700)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {modulo}
            </h3>
            <span style={{ fontSize: 11, color: 'var(--n-500)', marginLeft: 'auto' }}>
              {params.length} parámetro{params.length === 1 ? '' : 's'}
            </span>
          </div>

          <table className="data-table" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th>Clave</th>
                <th style={{ width: 140 }}>Valor</th>
                <th style={{ width: 80 }}>Tipo</th>
                <th style={{ width: 120 }}>Unidad</th>
                <th>Descripción</th>
                <th style={{ width: 90 }}>Editable</th>
              </tr>
            </thead>
            <tbody>
              {params.map((p) => (
                <tr key={p.id}>
                  <td className="mono" style={{ fontSize: 12 }}>{p.clave}</td>
                  <td className="mono" style={{ fontSize: 13, fontWeight: 500 }}>{p.valor ?? '—'}</td>
                  <td>
                    <span className="badge badge-muted">{p.tipo}</span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--n-600)' }}>{p.unidad ?? '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--n-600)', lineHeight: 1.4 }}>
                    {p.descripcion ?? '—'}
                  </td>
                  <td>
                    {p.editable ? (
                      <span className="badge badge-success">sí</span>
                    ) : (
                      <span className="badge badge-muted">no</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}
